import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Plus, Pencil, Trash2, Calendar, Eye, Clock, CheckCircle,
  AlertCircle, RefreshCw, X, Save, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY_FORM = {
  titulo: '', slug: '', resumen: '', contenido: '', content_html: '', imagen: '',
  categoria: 'legal', es_premium: false, publish_at: '',
  meta_title: '', meta_description: '', tags: '', read_time: ''
};

function slugify(text) {
  return text.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function AdminArticlesPage() {
  const { user, isAuthenticated } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editSlug, setEditSlug] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/articles`, { withCredentials: true });
      setArticles(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar artículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') fetchArticles();
  }, [isAuthenticated, user]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditSlug(null);
    setShowForm(true);
  };

  const openEdit = (article) => {
    setForm({
      titulo: article.titulo,
      slug: article.slug,
      resumen: article.resumen,
      contenido: article.contenido,
      content_html: article.content_html || '',
      imagen: article.imagen,
      categoria: article.categoria,
      es_premium: article.es_premium,
      publish_at: article.publish_at ? new Date(article.publish_at).toISOString().slice(0, 16) : '',
      meta_title: article.meta_title || '',
      meta_description: article.meta_description || '',
      tags: (article.tags || []).join(', '),
      read_time: article.read_time || ''
    });
    setEditSlug(article.slug);
    setShowForm(true);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/articles/${slug}`, { withCredentials: true });
      fetchArticles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null
      };
      if (editSlug) {
        await axios.put(`${API_URL}/api/admin/articles/${editSlug}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/articles`, payload, { withCredentials: true });
      }
      setShowForm(false);
      fetchArticles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, imagen: `${API_URL}${res.data.url}` }));
    } catch (err) {
      setError('Error al subir imagen');
    }
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-bold">Acceso denegado</h1>
          <Link to="/" className="text-rose-600 hover:underline mt-4 inline-block">Volver</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-articles-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={18} />
              <span className="text-sm">Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="font-bold text-slate-900">Gestión de Posts</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchArticles} className="p-2 hover:bg-slate-100 transition" data-testid="refresh-articles">
              <RefreshCw size={18} />
            </button>
            <button
              onClick={openNew}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 transition"
              data-testid="new-article-btn"
            >
              <Plus size={16} />
              Nuevo post
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => setError('')} className="ml-auto"><X size={16} /></button>
          </div>
        )}

        {/* Article Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto" data-testid="article-form-modal">
            <form onSubmit={handleSave} className="bg-white w-full max-w-3xl mx-4 mb-10 border border-slate-200 shadow-xl">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-bold text-slate-900 text-lg">{editSlug ? 'Editar post' : 'Nuevo post'}</h2>
                <button type="button" onClick={() => setShowForm(false)}><X size={20} /></button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase">Título</label>
                  <input
                    type="text" required value={form.titulo}
                    onChange={(e) => {
                      const titulo = e.target.value;
                      setForm(f => ({ ...f, titulo, slug: editSlug ? f.slug : slugify(titulo) }));
                    }}
                    className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="article-titulo"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase">Slug (URL)</label>
                  <input
                    type="text" required value={form.slug}
                    onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                    className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm font-mono"
                    data-testid="article-slug"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase">Resumen</label>
                  <textarea
                    rows={2} required value={form.resumen}
                    onChange={(e) => setForm(f => ({ ...f, resumen: e.target.value }))}
                    className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                    data-testid="article-resumen"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase">Contenido (Markdown)</label>
                  <textarea
                    rows={6} value={form.contenido}
                    onChange={(e) => setForm(f => ({ ...f, contenido: e.target.value }))}
                    className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm font-mono"
                    data-testid="article-contenido"
                    placeholder="Contenido en formato Markdown (alternativa al HTML)"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase">Contenido HTML (alternativo)</label>
                  <textarea
                    rows={6} value={form.content_html}
                    onChange={(e) => setForm(f => ({ ...f, content_html: e.target.value }))}
                    className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm font-mono"
                    data-testid="article-content-html"
                    placeholder="Pegar HTML del artículo (tiene prioridad sobre Markdown)"
                  />
                </div>

                {/* Image: URL or upload */}
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase">Imagen destacada</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="url" value={form.imagen}
                      onChange={(e) => setForm(f => ({ ...f, imagen: e.target.value }))}
                      className="flex-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      data-testid="article-imagen"
                      placeholder="URL de la imagen o subir archivo"
                    />
                    <label className="px-4 py-3 bg-slate-100 border border-slate-200 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-200 transition flex-shrink-0">
                      Subir
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="article-image-upload" />
                    </label>
                  </div>
                  {form.imagen && (
                    <img src={form.imagen} alt="Preview" className="mt-2 h-24 object-cover border border-slate-200" />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-600 uppercase">Categoría</label>
                    <select
                      value={form.categoria}
                      onChange={(e) => setForm(f => ({ ...f, categoria: e.target.value }))}
                      className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      data-testid="article-categoria"
                    >
                      <option value="legal">Legal</option>
                      <option value="normativa">Normativa</option>
                      <option value="prl">PRL</option>
                      <option value="trabajadores">Trabajadores</option>
                      <option value="empresas">Empresas</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-600 uppercase">Tiempo de lectura</label>
                    <input
                      type="text" value={form.read_time}
                      onChange={(e) => setForm(f => ({ ...f, read_time: e.target.value }))}
                      className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      placeholder="ej: 8 min de lectura"
                      data-testid="article-read-time"
                    />
                  </div>
                </div>

                {/* SEO Fields */}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-3">SEO</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-bold text-slate-600 uppercase">Meta título</label>
                      <input
                        type="text" value={form.meta_title}
                        onChange={(e) => setForm(f => ({ ...f, meta_title: e.target.value }))}
                        className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                        placeholder="Título para Google (max 60 car.)"
                        data-testid="article-meta-title"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-600 uppercase">Meta descripción</label>
                      <textarea
                        rows={2} value={form.meta_description}
                        onChange={(e) => setForm(f => ({ ...f, meta_description: e.target.value }))}
                        className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                        placeholder="Descripción para Google (max 160 car.)"
                        data-testid="article-meta-description"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-600 uppercase">Tags (separados por coma)</label>
                      <input
                        type="text" value={form.tags}
                        onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                        className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                        placeholder="baja laboral, salud mental, PRL"
                        data-testid="article-tags"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-600 uppercase flex items-center gap-2">
                      <Clock size={14} />
                      Programar publicación
                    </label>
                    <input
                      type="datetime-local"
                      value={form.publish_at}
                      onChange={(e) => setForm(f => ({ ...f, publish_at: e.target.value }))}
                      className="w-full mt-1 p-3 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                      data-testid="article-publish-at"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Dejar vacío para publicar inmediatamente</p>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox" checked={form.es_premium}
                        onChange={(e) => setForm(f => ({ ...f, es_premium: e.target.checked }))}
                        className="w-4 h-4 accent-rose-600"
                        data-testid="article-premium"
                      />
                      <span className="text-sm text-slate-600">Contenido premium</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">
                  Cancelar
                </button>
                <button
                  type="submit" disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 transition disabled:opacity-50"
                  data-testid="save-article-btn"
                >
                  <Save size={16} />
                  {saving ? 'Guardando...' : (editSlug ? 'Actualizar' : 'Crear post')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Articles List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600" />
          </div>
        ) : (
          <div className="space-y-3" data-testid="articles-list">
            {articles.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 text-center">
                <p className="text-slate-400">No hay artículos</p>
              </div>
            ) : (
              articles.map((article) => (
                <div
                  key={article.id || article.slug}
                  className="bg-white border border-slate-200 p-4 flex items-center gap-4"
                  data-testid={`admin-article-${article.slug}`}
                >
                  {article.imagen && (
                    <img src={article.imagen} alt="" className="w-20 h-14 object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 ${
                        article.status === 'programado' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {article.status === 'programado' ? <Clock size={10} /> : <CheckCircle size={10} />}
                        {article.status}
                      </span>
                      <span className="text-[10px] text-slate-400 uppercase">{article.categoria}</span>
                      {article.es_premium && <span className="text-[10px] bg-rose-100 text-rose-600 px-2 py-0.5 font-bold">PREMIUM</span>}
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm truncate">{article.titulo}</h3>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                      <span>{new Date(article.created_at).toLocaleDateString('es-ES')}</span>
                      {article.publish_at && (
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          Programado: {new Date(article.publish_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link
                      to={`/blog/${article.slug}`}
                      target="_blank"
                      className="p-2 hover:bg-slate-100 transition text-slate-500"
                      title="Ver"
                    >
                      <Eye size={16} />
                    </Link>
                    <button onClick={() => openEdit(article)} className="p-2 hover:bg-slate-100 transition text-slate-500" title="Editar" data-testid={`edit-${article.slug}`}>
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDelete(article.slug)} className="p-2 hover:bg-red-50 transition text-red-400" title="Eliminar" data-testid={`delete-${article.slug}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
